import { GetPermissionsResp, ListRoleResp } from '@/interfaces/role';
import { getAgentPermissions, getListRoles } from '@/apis/role';
import { atom } from 'jotai';
import { errorHandler } from '@/utils/request';
import {
  getCachedPermissions,
  setCachedPermissions,
  isPermissionsEqual,
  clearPermissionsCache,
} from '@/utils/permissions-cache';

export const agentPermissionsDataAtom = atom<GetPermissionsResp | null>(null);

// 添加一个标记 atom 来防止重复加载
export const permissionsLoadingAtom = atom<boolean>(false);

// 创建一个自动加载的 atom
export const agentPermissionsAtom = atom(
  (get) => {
    const agentPermissions = get(agentPermissionsDataAtom);
    const isLoading = get(permissionsLoadingAtom);

    return { agentPermissions, isLoading };
  },
  (get, set) => {
    // 设置加载状态
    set(permissionsLoadingAtom, true);

    // 首先尝试从缓存获取数据
    const cachedData = getCachedPermissions();

    if (cachedData) {
      // 如果有缓存，立即设置到 atom 中
      set(agentPermissionsDataAtom, cachedData);
    }

    // 无论是否有缓存，都发起接口请求获取最新数据
    return getAgentPermissions(undefined)
      .then((freshData) => {
        // 如果接口返回的数据与缓存不一致，更新缓存和 atom
        if (!cachedData || !isPermissionsEqual(cachedData, freshData)) {
          setCachedPermissions(freshData);
          set(agentPermissionsDataAtom, freshData);
        }
        set(permissionsLoadingAtom, false);
        return freshData;
      })
      .catch((error) => {
        // 如果接口请求失败，但有缓存数据，继续使用缓存
        if (cachedData) {
          set(permissionsLoadingAtom, false);
          return cachedData;
        }
        // 如果没有缓存且接口失败，抛出错误
        set(permissionsLoadingAtom, false);
        errorHandler(error);
        throw error;
      });
  },
);

// 角色数据管理
export const rolesDataAtom = atom<ListRoleResp[]>([]);
export const rolesLoadingAtom = atom<boolean>(false);

export const rolesAtom = atom(
  (get) => ({
    roles: get(rolesDataAtom),
    loading: get(rolesLoadingAtom),
  }),
  (get, set) => {
    set(rolesLoadingAtom, true);
    return getListRoles()
      .then((roles) => {
        set(rolesDataAtom, roles);
        set(rolesLoadingAtom, false);
        return roles;
      })
      .catch((error) => {
        set(rolesLoadingAtom, false);
        errorHandler(error);
        throw error;
      });
  },
);

// 清除权限缓存的 atom
export const clearPermissionsCacheAtom = atom(null, (get, set) => {
  clearPermissionsCache();
  set(agentPermissionsDataAtom, null);
});
